import { useMemo } from "react";

import { $enum } from "ts-enum-util";

import { FrostFreeStatus, type PatchBuildingByIdRequestBody } from "@eisox/api/core-api";
import { Alert, Field } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { getDirtyValues } from "@eisox/tools";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { useSelector } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { buildingApi } from "~/api";
import { DEFAULT_FROST_FREE_TEMPERATURE } from "~/constants/building.constant";
import { DEFAULT_TEMPORISATION_FROST_FREE_MODE } from "~/constants/season.constant";
import { TEMPERATURE_MAX, TEMPERATURE_MIN, TEMPERATURE_STEP } from "~/constants/temperature.constant";
import {
  filterPeriods,
  frostFreePeriodsAllowed,
  getFrostFreeStatusLabel,
  Periods,
  type PeriodsType,
} from "~/features/frost-free";
import { getDefaultValues, updateHouseHollidaysSettingsSchema } from "~/features/settings";
import { clampTemperature, getFrostFreeTemperatureBounds } from "~/helpers/temperature.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/buildings/holidays")({
  component: RouteComponent,
});

const getPeriodErrorMessage = (itemError: unknown): string | undefined => {
  if (typeof itemError === "string") return itemError;
  if (itemError && typeof itemError === "object" && "message" in itemError) {
    return String(itemError.message);
  }
  return undefined;
};

const mapPeriodsWithErrors = (
  periods: PeriodsType,
  fieldErrors: unknown[] | undefined,
): (PeriodsType[number] & { error?: string })[] =>
  periods.map((period, index) => ({
    ...period,
    error: getPeriodErrorMessage(fieldErrors?.[index]),
  }));

function RouteComponent() {
  const { building, boilerRooms, gateways } = useBuildingContext(Route.id);

  const queryClient = useQueryClient();

  const { mutateAsync: update } = useMutation({
    mutationFn: (body: PatchBuildingByIdRequestBody) => buildingApi.update(building.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries(buildingApi.queries.byId(building.id));
    },
  });

  const areFrostFreePeriodsAllowed = frostFreePeriodsAllowed(gateways);
  const showBoilerRoomSettings = areFrostFreePeriodsAllowed && boilerRooms.length > 0;

  const frostFreeTemperatureBounds = getFrostFreeTemperatureBounds({
    absenceTemperature: building.absenceTemperature,
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(building, areFrostFreePeriodsAllowed),
    validators: {
      onSubmit: updateHouseHollidaysSettingsSchema,
    },
    onSubmit: async ({ formApi, value }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      const { mode, frostFreePeriods, ...scalarDirty } = dirty;
      await update({
        ...scalarDirty,
        ...((mode !== undefined || frostFreePeriods !== undefined) && {
          frostFree: { status: value.mode, periods: filterPeriods(value.frostFreePeriods) },
        }),
      });
      formApi.reset();
    },
  });

  const mode = useSelector(form.store, state => state.values.mode);
  const frostFreeTemperature =
    useSelector(form.store, state => state.values.frostFreeTemperature) ?? DEFAULT_FROST_FREE_TEMPERATURE;
  const temporisationFrostFreeMode =
    useSelector(form.store, state => state.values.temporisationFrostFreeMode) ?? DEFAULT_TEMPORISATION_FROST_FREE_MODE;
  const setpointStartFrostFree = useSelector(form.store, state => state.values.setpointStartFrostFree);
  const reducedFFTemperature = useSelector(form.store, state => state.values.reducedFFTemperature);

  const modeOptions = useMemo(
    () =>
      $enum(FrostFreeStatus)
        .getValues()
        .filter(status => areFrostFreePeriodsAllowed || status !== FrostFreeStatus.PERIODS)
        .map(status => ({ value: status, name: getFrostFreeStatusLabel(status) })),
    [areFrostFreePeriodsAllowed],
  );

  return (
    <form
      className="gap-x-10 grid h-full grid-cols-[minmax(min-content,500px)_minmax(min-content,450px)] overflow-auto bg-primary-white text-base whitespace-pre-line"
      data-testid="HouseHolidaysSettings"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-[10px]">
          <Alert severity="help" data-testid={"help"}>
            {m.building_holidays_settings_help()}
          </Alert>
          <form.AppField name="mode">
            {field => (
              <field.Select
                classNames={{ label: "text-primary-blue", control: "w-full" }}
                multiple={false}
                label={m.holidays()}
                options={modeOptions}
              />
            )}
          </form.AppField>
          {mode === FrostFreeStatus.PERIODS && (
            <form.AppField name="frostFreePeriods" mode="array">
              {field => (
                <Field.Root className="flex flex-col gap-[10px]">
                  <Field.Label className="text-primary-blue">{m.holidays_periods()}</Field.Label>
                  <Periods
                    periods={mapPeriodsWithErrors(field.state.value, field.state.meta.errors)}
                    onAddPeriod={period => field.pushValue(period)}
                    onRemovePeriod={index => field.removeValue(index)}
                    onUpdatePeriod={(index, period) => field.replaceValue(index, period)}
                  />
                </Field.Root>
              )}
            </form.AppField>
          )}
        </div>
        <div className="flex flex-col gap-[10px]">
          <Field.Root>
            <Field.Label>{m.settings()}</Field.Label>
            <p>
              <ParaglideMessage
                message={m.building_holidays_settings_text_1}
                inputs={{ ff: frostFreeTemperature }}
                markup={{
                  span: ({ children }) => <span className="italic">{children}</span>,
                }}
              />
              {areFrostFreePeriodsAllowed && (
                <>
                  <br />
                  <ParaglideMessage
                    message={m.building_holidays_settings_text_2}
                    inputs={{ ff: frostFreeTemperature, temp: temporisationFrostFreeMode }}
                    markup={{
                      span: ({ children }) => <span className="italic">{children}</span>,
                    }}
                  />
                </>
              )}
            </p>
          </Field.Root>
          <form.AppField name="frostFreeTemperature">
            {field => (
              <field.Slider
                label={`${m.frost_free_temperature()} (°C)`}
                min={TEMPERATURE_MIN}
                max={TEMPERATURE_MAX}
                step={TEMPERATURE_STEP}
                valueLabelDisplay="auto"
                valueLabelFormat={`${m.frost_free()} ${field.state.value}°C < ${m.absence()} ${building.absenceTemperature}°C`}
                onValueChange={nextValue => {
                  if (nextValue === undefined) return;
                  field.handleChange(clampTemperature(nextValue, frostFreeTemperatureBounds));
                }}
              />
            )}
          </form.AppField>
          {showBoilerRoomSettings && (
            <>
              <form.AppField name="temporisationFrostFreeMode">
                {field => <field.Slider label={`${m.temporisation_frost_free_mode()} (h)`} min={0} max={20} step={1} />}
              </form.AppField>
              <p>
                <ParaglideMessage
                  message={m.building_holidays_settings_text_3}
                  inputs={{ ext: setpointStartFrostFree ?? 0, temp: temporisationFrostFreeMode }}
                  markup={{
                    span: ({ children }) => <span className="italic">{children}</span>,
                  }}
                />
              </p>
              <form.AppField name="setpointStartFrostFree">
                {field => (
                  <field.Slider
                    label={`${m.setpoint_start_frost_free()} (°C)`}
                    min={TEMPERATURE_MIN}
                    max={TEMPERATURE_MAX}
                    step={TEMPERATURE_STEP}
                  />
                )}
              </form.AppField>
              <p>
                <ParaglideMessage
                  message={m.building_holidays_settings_text_4}
                  inputs={{ rfft: reducedFFTemperature ?? 0 }}
                  markup={{
                    span: ({ children }) => <span className="italic">{children}</span>,
                  }}
                />
              </p>
              <form.AppField name="reducedFFTemperature">
                {field => (
                  <field.Slider
                    label={`${m.reduced_frost_free_temperature()} (°C)`}
                    min={TEMPERATURE_MIN}
                    max={100}
                    step={TEMPERATURE_STEP}
                  />
                )}
              </form.AppField>
            </>
          )}
        </div>
        <div className="mt-10 gap-5 col-span-2 flex flex-col items-center justify-end">
          <form.Subscribe
            selector={state => ({
              mode: state.values.mode,
              isModeDirty: state.fieldMeta.mode?.isDirty ?? false,
            })}
          >
            {({ mode: currentMode, isModeDirty }) =>
              currentMode === FrostFreeStatus.ENABLED && isModeDirty ? (
                <Alert severity="warning">{m.building_holidays_settings_warning()}</Alert>
              ) : null
            }
          </form.Subscribe>
          <form.SubmitButton label={m.save()} />
        </div>
      </form.AppForm>
    </form>
  );
}
